import { Stack } from '@mui/material';
import { SelectField } from '@resurs-internal/mui';
import { SelectFieldOption } from '@resurs-internal/mui/components/SelectField/SelectFieldOption';
import { useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import useTranslation from '../../hooks/useTranslation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { hideNativeSelectArrowSx } from '../SelectField/SelectField';
import Subtitle from '../Subtitle/Subtitle';
import ValidationError from '../ValidationError/ValidationError';

const MONTH_IDS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const splitStoredValue = (storedValue: string | number | boolean | string[]): [string, string] => {
    if (typeof storedValue === 'string' && storedValue.includes('-')) {
        const [year, month] = storedValue.split('-');
        return [year, month];
    }
    return ['', ''];
};

type Props = {
    name: string;
    label: string;
    yearRange: { from: number; to: number };
};

const YearMonthField = ({ name, label, yearRange }: Props) => {
    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const storedValue = useAppSelector(selectFieldValue)(name);

    // Year/month are tracked locally rather than re-derived from `storedValue` on every
    // render: only one dropdown changes per user interaction, so the combined "YYYY-MM"
    // isn't dispatched until both halves are picked, and re-deriving from the (still
    // incomplete) stored value would make the just-picked half appear to revert to blank.
    const [year, setYear] = useState(() => splitStoredValue(storedValue)[0]);
    const [month, setMonth] = useState(() => splitStoredValue(storedValue)[1]);

    useEffect(() => {
        if (!storedValue) {
            setYear('');
            setMonth('');
        }
    }, [storedValue]);

    const yearOptions: SelectFieldOption[] = useMemo<SelectFieldOption[]>(
        () =>
            Array.from({ length: yearRange.to - yearRange.from + 1 }, (_, index) => {
                const value = String(yearRange.to - index);
                return { id: value, label: value };
            }),
        [yearRange.from, yearRange.to],
    );

    const monthOptions: SelectFieldOption[] = useMemo<SelectFieldOption[]>(
        () => MONTH_IDS.map((id) => ({ id, label: translate(`field.month.option.${id}`) })),
        [translate],
    );

    const commit = (nextYear: string, nextMonth: string) => {
        setYear(nextYear);
        setMonth(nextMonth);
        dispatch(
            addOrUpdateFormField({
                fieldName: name,
                value: nextYear && nextMonth ? `${nextYear}-${nextMonth}` : undefined,
                hasError: false,
            }),
        );
    };

    return (
        <>
            <Subtitle text={label} />
            <Stack direction="row" spacing={2}>
                <SelectField
                    sx={hideNativeSelectArrowSx}
                    label={translate('field.year.label')}
                    value={year}
                    outline={'single'}
                    options={yearOptions}
                    onChange={(e: { target: { value: unknown } }) => commit(e.target.value as string, month)}
                    aria-label={translate('field.year.label')}
                />
                <SelectField
                    sx={hideNativeSelectArrowSx}
                    label={translate('field.month.label')}
                    value={month}
                    outline={'single'}
                    options={monthOptions}
                    onChange={(e: { target: { value: unknown } }) => commit(year, e.target.value as string)}
                    aria-label={translate('field.month.label')}
                />
            </Stack>
            <ValidationError fieldName={name} />
        </>
    );
};

export default YearMonthField;
